import {
  MAX_REP_TARGET,
  resolveStrengthRepFields,
} from "../workoutBuilder/repPrescription";

// Tier-3 builder presentation seam.
//
// The strength rep prescription. There is NO mode selector: the target type is
// inferred from which of the two fields the user has filled.
//
//   [        ]            no target
//   [ 8 ]  [ 8 ghost ]    fixed reps -- the right field only suggests
//   [ 6 ]  [ 8 ]          rep range  -- left is MIN, right is MAX
//
// The ghost is a placeholder, never a value: mounting, focusing or blurring it
// writes nothing, so a fixed 8 can never drift into an 8-8 range on its own.
// Only a value the user actually types into the right field makes it a range.
//
// This control is REPS-only. A legacy persisted `targetSeconds` on a REPS
// exercise is surfaced read-only so it is neither invisible nor silently
// rewritten; the user converts it by typing a rep value, which the Phase 6A
// mutator writes atomically across all four fields. TIMED_HOLD uses the
// dedicated HoldSecondsControl instead.
//
// Presentation only. The container owns `repsDrafts`, the commit timing and
// every mutation; this component renders inputs and reports focus/change/blur
// per slot.
function joinClassNames(...values) {
  return values.filter(Boolean).join(" ");
}

function NumberField({
  value,
  placeholder,
  disabled,
  ariaLabel,
  min,
  max,
  onFocus,
  onChange,
  onBlur,
  variant,
}) {
  return (
    <input
      type="number"
      inputMode="numeric"
      disabled={disabled}
      min={min}
      max={max}
      step="1"
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onFocus={onFocus}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      className={joinClassNames(
        "lz-builder-rep-field",
        variant && `lz-builder-rep-field--${variant}`
      )}
    />
  );
}

export default function RepPrescriptionControl({
  prescription,
  drafts,
  disabled = false,
  label,
  onFieldFocus,
  onFieldChange,
  onFieldBlur,
}) {
  const fields = resolveStrengthRepFields(prescription);

  // Draft slots are stable across the implicit fixed <-> range transition:
  // "reps" is always the left field and "maxReps" always the right one, so a
  // value being typed is never lost because the inferred mode changed.
  const slotProps = (slot, persistedValue) => ({
    value:
      drafts?.[slot] ?? (persistedValue == null ? "" : String(persistedValue)),
    disabled,
    onFocus: () => onFieldFocus(slot),
    onChange: (nextValue) => onFieldChange(slot, nextValue),
    onBlur: () => onFieldBlur(slot),
  });

  const secondaryDraft = drafts?.maxReps;
  // The right field stays mounted while it holds a draft, so it cannot vanish
  // under the user mid-edit after they clear it.
  const showSecondary =
    fields.hasSecondaryField || fields.isRange || secondaryDraft != null;
  const isSecondaryGhosted =
    !fields.isRange && (secondaryDraft == null || secondaryDraft === "");

  return (
    <div className="lz-builder-rep-control">
      <div className="lz-builder-rep-control__values">
        <NumberField
          {...slotProps("reps", fields.primary)}
          ariaLabel={`${label} reps`}
          min="0"
          max={String(MAX_REP_TARGET)}
          variant="primary"
        />

        {showSecondary ? (
          <span
            className={joinClassNames(
              "lz-builder-rep-control__secondary",
              isSecondaryGhosted && "lz-builder-rep-control__secondary--ghost"
            )}
            data-ghost={isSecondaryGhosted ? "true" : undefined}
          >
            <span
              className="lz-builder-rep-control__separator"
              aria-hidden="true"
            >
              &ndash;
            </span>
            <NumberField
              {...slotProps("maxReps", fields.secondary)}
              placeholder={fields.ghost == null ? undefined : String(fields.ghost)}
              ariaLabel={`${label} maximum reps`}
              min="1"
              max={String(MAX_REP_TARGET)}
              variant="bound"
            />
          </span>
        ) : null}
      </div>

      {fields.legacySeconds == null ? null : (
        <span className="lz-builder-rep-control__legacy">
          {fields.legacySeconds} sec
        </span>
      )}
    </div>
  );
}
